import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Star, Image as ImageIcon, Loader2, X } from "lucide-react";
import api from "@/lib/api";

/**
 * Two-mode review form:
 * - `direction="customer_to_provider"`: quality, communication, timeliness, value
 * - `direction="provider_to_customer"`: accuracy, preparedness, payment_cooperation, respectfulness
 *
 * Only the "overall" rating is required by the API. Photos are uploaded as
 * MediaFiles via /files/upload before we submit the review.
 */
export default function ReviewForm({ transaction, direction, onSubmitted }) {
  const isC2P = direction === "customer_to_provider";
  const [overall, setOverall] = useState(5);
  const [metrics, setMetrics] = useState(
    isC2P
      ? { quality: 5, communication: 5, timeliness: 5, value: 5, professionalism: 5 }
      : { accuracy: 5, preparedness: 5, payment_cooperation: 5, respectfulness: 5, communication: 5 }
  );
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [privateFeedback, setPrivateFeedback] = useState("");
  const [displayWork, setDisplayWork] = useState(true);
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const setMetric = (k, v) => setMetrics((m) => ({ ...m, [k]: v }));

  const onPickPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const added = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", "review");
      try {
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ id: data.id, name: data.original_filename, url: data.url });
      } catch (err) {
        toast.error(err?.response?.data?.error || `Could not upload ${f.name}`);
      }
    }
    setImages((prev) => [...prev, ...added]);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeImage = (id) => setImages((prev) => prev.filter((x) => x.id !== id));

  const submit = async () => {
    if (!transaction?.id) return;
    setSubmitting(true);
    try {
      const idField = transaction.target_collection === "orders" ? "order_id" : "booking_id";
      const payload = {
        [idField]: transaction.id,
        overall,
        ...metrics,
        title: title || null,
        body,
        image_ids: images.map((i) => i.id),
        permission_to_display_work: displayWork,
        private_feedback: privateFeedback || null,
      };
      await api.post("/reviews", payload);
      toast.success("Thanks — your review is live.");
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not save review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6" data-testid="review-form">
      <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Leave a review</div>
      <div className="mt-1 font-display text-xl font-semibold text-[#071b22]">
        {isC2P ? "How was your experience?" : "How was working with this customer?"}
      </div>
      <div className="text-sm text-[#252A34]/70">Reviews only unlock on completed transactions and are visible to future customers.</div>

      {/* Overall rating */}
      <div className="mt-5">
        <div className="text-sm font-medium text-[#071b22]">Overall rating</div>
        <StarPicker value={overall} onChange={setOverall} big testId="review-overall" />
      </div>

      {/* Metric ratings */}
      <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="review-metrics">
        {Object.entries(metrics).map(([k, v]) => (
          <div key={k}>
            <div className="text-xs text-[#252A34]/70 capitalize">{k.replace(/_/g, " ")}</div>
            <StarPicker value={v} onChange={(nv) => setMetric(k, nv)} testId={`review-metric-${k}`} />
          </div>
        ))}
      </div>

      <div className="mt-5">
        <label className="text-xs text-[#252A34]/70">Title (optional)</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={80}
          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#165a71]"
          data-testid="review-title"
          placeholder="A brief headline for your review"
        />
      </div>

      <div className="mt-3">
        <label className="text-xs text-[#252A34]/70">Public review</label>
        <textarea
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#165a71]"
          data-testid="review-body"
          placeholder={isC2P ? "Tell others about your experience…" : "Would you work with this customer again?"}
        />
      </div>

      {isC2P && (
        <div className="mt-3">
          <div className="text-xs text-[#252A34]/70 mb-2">Photos (optional)</div>
          <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={onPickPhoto} data-testid="review-photo-input" />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium hover:bg-slate-50"
            data-testid="review-photo-picker"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
            {uploading ? "Uploading…" : "Add photos"}
          </button>
          {images.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {images.map((img) => (
                <div key={img.id} className="relative">
                  <img src={img.url} alt={img.name} className="h-16 w-16 rounded-lg object-cover border border-slate-200" />
                  <button
                    type="button"
                    onClick={() => removeImage(img.id)}
                    className="absolute -top-1.5 -right-1.5 rounded-full bg-white shadow border border-slate-200 h-5 w-5 inline-flex items-center justify-center"
                    aria-label="Remove"
                    data-testid={`review-photo-remove-${img.id}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-4">
        <label className="text-xs text-[#252A34]/70">Private feedback to Gulf Coast Work (optional)</label>
        <textarea
          rows={2}
          value={privateFeedback}
          onChange={(e) => setPrivateFeedback(e.target.value)}
          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#165a71]"
          data-testid="review-private-feedback"
          placeholder="This won't be shown publicly."
        />
      </div>

      {isC2P && (
        <label className="mt-3 inline-flex items-center gap-2 text-xs text-[#252A34]/80">
          <input
            type="checkbox"
            checked={displayWork}
            onChange={(e) => setDisplayWork(e.target.checked)}
            data-testid="review-permission-photos"
          />
          Allow this pro to feature my photos in their portfolio.
        </label>
      )}

      <div className="mt-6 flex items-center gap-2">
        <button
          disabled={submitting || !body}
          onClick={submit}
          className="rounded-full bg-[#165a71] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#11485a] disabled:opacity-50 inline-flex items-center gap-2"
          data-testid="review-submit"
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          Publish review
        </button>
        <div className="text-xs text-[#252A34]/60">One review per completed transaction. Cannot be edited once posted.</div>
      </div>
    </div>
  );
}

function StarPicker({ value, onChange, big = false, testId }) {
  const size = big ? "h-8 w-8" : "h-5 w-5";
  return (
    <div className="inline-flex items-center gap-1" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          data-testid={`${testId}-star-${v}`}
          className="focus:outline-none"
          aria-label={`Rate ${v} of 5`}
        >
          <Star className={`${size} transition-colors ${v <= value ? "text-amber-500 fill-amber-500" : "text-slate-300"}`} />
        </button>
      ))}
      {big && <span className="ml-2 font-display text-lg text-[#071b22] font-semibold">{value}.0</span>}
    </div>
  );
}
